import { useState, useEffect, memo } from 'react';
import { Card, Button, Skeleton, useSpinner, InfoTooltip } from '@chikara/ui';
import { formatCurrency } from '@chikara/utils';
import { t } from '@/i18n';
import type { CreditSummary } from '../../types/credits.types';
import styles from './CreditSummarySection.module.css';

export interface CreditSummarySectionProps {
  data: CreditSummary;
  onMakePayment?: () => void;
}

// --- Utils ---
const formatMXN = (value: number) =>
  formatCurrency(value, { locale: 'es-MX', currency: 'MXN' });

/** Etiqueta con ícono de ayuda (?) y su tooltip. */
const LabelWithTooltip = ({ text, tooltip }: { text: string; tooltip: string }) => (
  <span className={styles.labelWithInfo}>
    {text}
    <InfoTooltip content={tooltip} label={text} />
  </span>
);

// --- Sub-components ---

const CreditSummarySkeleton = memo(({ isMobile }: { isMobile: boolean }) => (
  <div className={styles.wrapper}>
    {!isMobile ? (
      <>
        <Card className={styles.creditLineCard}>
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Skeleton width="100px" height="14px" />
            <Skeleton width="180px" height="28px" />
          </div>
        </Card>
        <Card className={styles.detailsCard}>
          <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', padding: '1rem', gap: '1rem' }}>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Skeleton width="80px" height="14px" />
                <Skeleton width="120px" height="24px" />
             </div>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Skeleton width="80px" height="14px" />
                <Skeleton width="120px" height="24px" />
             </div>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Skeleton width="80px" height="14px" />
                <Skeleton width="120px" height="24px" />
             </div>
             <Skeleton width="140px" height="48px" style={{ borderRadius: '8px' }} />
          </div>
        </Card>
      </>
    ) : (
      <Card className={styles.mainCard}>
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Skeleton width="150px" height="18px" />
          <Skeleton width="100%" height="60px" />
          <Skeleton width="100%" height="60px" />
        </div>
      </Card>
    )}
  </div>
));

const CreditSummaryMobile = memo(({ 
  data, 
  onMakePayment, 
  isExpanded, 
  onToggle 
}: CreditSummarySectionProps & { isExpanded: boolean; onToggle: () => void }) => (
  <div className={styles.wrapper}>
    <Card className={styles.mainCard}>
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderTitle}>{t('credits.summaryTitle')}</span>
        <button 
          type="button" 
          className={styles.headerPayButton}
          onClick={onToggle}
        >
          {isExpanded ? t('common.viewLess') : t('common.viewMore')}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.cardDetails}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>
              <LabelWithTooltip text={t('credits.creditLineLabel')} tooltip={t('tooltips.creditLine')} />
            </span>
            <span className={styles.detailValue}>{formatMXN(data.creditLine)}</span>
          </div>

          <div className={styles.splitRow}>
            <div className={styles.splitColumn}>
              <span className={styles.detailLabel}>
                <LabelWithTooltip text={t('credits.spentLabel')} tooltip={t('tooltips.spentCompany')} />
              </span>
              <span className={styles.detailValue}>{formatMXN(data.spent)}</span>
            </div>
            <div className={styles.splitColumn}>
              <span className={styles.detailLabel}>
                <LabelWithTooltip text={t('credits.availableLabel')} tooltip={t('tooltips.availableCompany')} />
              </span>
              <span className={styles.detailValue}>{formatMXN(data.available)}</span>
            </div>
          </div>

          <div className={styles.paymentRow}>
            <span className={styles.detailLabel}>
              <LabelWithTooltip text={t('credits.toPayLabel')} tooltip={t('tooltips.toPay')} />
            </span>
            <div className={styles.paymentContent}>
              <span className={styles.detailValue}>{formatMXN(data.toPayAmount)}</span>
              <button 
                type="button" 
                className={styles.redPayButton}
                onClick={onMakePayment}
              >
                {t('credits.makePayment')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  </div>
));

const CreditSummaryDesktop = memo(({ data, onMakePayment }: CreditSummarySectionProps) => (
  <section className={styles.section} aria-labelledby="credit-summary-title">
    <h2 id="credit-summary-title" className={styles.sectionTitle}>
      {t('credits.summaryTitle')}
    </h2>
    <div className={styles.wrapper}>
      <Card className={styles.creditLineCard}>
        <Card.Stats
          label={<LabelWithTooltip text={t('credits.creditLineLabel')} tooltip={t('tooltips.creditLine')} />}
          value={formatMXN(data.creditLine)}
        />
      </Card>
      <Card className={styles.detailsCard}>
        <Card.Stats
          label={<LabelWithTooltip text={t('credits.spentLabel')} tooltip={t('tooltips.spentCompany')} />}
          value={formatMXN(data.spent)}
          className={styles.statItem}
        />
        <Card.Stats
          label={<LabelWithTooltip text={t('credits.availableLabel')} tooltip={t('tooltips.availableCompany')} />}
          value={formatMXN(data.available)}
          className={styles.statItem}
        />
        <Card.Stats
          label={
            <span className={styles.toPayLabel}>
              <LabelWithTooltip text={t('credits.toPayLabel')} tooltip={t('tooltips.toPay')} />
              <span className={styles.toPayDate}>{data.toPayDate}</span>
            </span>
          }
          value={formatMXN(data.toPayAmount)}
          className={styles.statItem}
        />
        <Button
          type="button"
          variant="primary"
          size="lg"
          className={styles.payButton}
          onClick={onMakePayment}
          aria-label={t('credits.makePayment')}
        >
          {t('credits.makePayment')}
        </Button>
      </Card>
    </div>
  </section>
));

// --- Main Component ---

export function CreditSummarySection({ data, onMakePayment }: CreditSummarySectionProps) {
  const { isSpinnerLoading } = useSpinner();
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isSpinnerLoading) {
    return <CreditSummarySkeleton isMobile={isMobile} />;
  }

  if (isMobile) {
    return (
      <CreditSummaryMobile 
        data={data} 
        onMakePayment={onMakePayment} 
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />
    );
  }

  return <CreditSummaryDesktop data={data} onMakePayment={onMakePayment} />;
}

CreditSummarySection.displayName = 'CreditSummarySection';
CreditSummarySkeleton.displayName = 'CreditSummarySkeleton';
CreditSummaryMobile.displayName = 'CreditSummaryMobile';
CreditSummaryDesktop.displayName = 'CreditSummaryDesktop';
